// @givmo/passkeys — setting up an authenticator app on the web (AS-WEB14; D-AS-21's A2 and A3; QR by
// `qr@0.7.0`, D-AS-22).
//
// OFFERED ONLY to an account that signs in with Google, Apple or Microsoft, and only while the
// backend serves authenticator apps (`authenticatorAppOffered`, D-AS-26 N2, D-AS-27 R11).
//
// THE ORDER (iOS `TotpEnrollmentViewModel.swift`, AS-IOS3):
//   1. A FRESH PROVIDER SESSION. Identity Platform enrols only from a session whose own first factor
//      it accepts, and only a recent one; the web accepts Google, Apple and Microsoft sessions only.
//      So a suite handoff, a passkey sign-in or a passkey re-confirmation (all custom-token sessions),
//      a password session, or a stale one, confirms first through the "Confirm it's you" sheet with
//      the account's own Google, Apple or Microsoft ways — never the passkey arm, whose
//      re-confirmation is itself a custom-token session (the gate's `clientRefusal`, ruled for cut
//      1003c). The gate retries the start once the session is re-issued under the provider.
//   2. THE SECRET: the QR (a computer) or the setup link (a phone), and the key as text either way.
//   3. THE CODE, then "Turn on". A refused code clears and keeps the secret: the app already holds it.
//   4. The server's mirror is synced at once (`POST /users/me/security/factors/sync`), and ONLY THEN
//      are the recovery codes minted (`POST /users/me/security/recovery-codes`): codes for a factor
//      that does not exist would be a false claim, and the backend refuses them. Shown once (A3).
//
// The person's authenticator, its key and the codes never leave this page's memory: nothing is
// stored or logged, and the enrolment's Firebase secret lives in `@givmo/auth`'s closure.
//
// UNDER A SIGN-IN LOCK NOTHING STARTS (AS-WEB23): a setup pressed on a page drawn before the lock
// bound (a stale tab) re-reads the security read first and ends `locked` when it says so, before any
// sheet; the fresh read redraws the surface locked. A re-read that FAILS starts nothing either and
// ends `failed` with the hook's failure line; the dark 404 goes on as before. A session from before
// the lock can still enrol, and the lock would refuse every sign-in that factor could ever finish.

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, ErrorMessage, Icon, OtpField, cn, testIdProp } from '@givmo/design-system';
import { isGivmoError, sdk } from '@givmo/api-client';
import { AuthError, canEnrollTotpFrom, type TotpEnrollment } from '@givmo/auth';
import {
  isStepUpCancelled,
  useStepUpGate,
  type StepUpClientRefusal,
  type StepUpCopyTable,
} from '@givmo/step-up';
import { Copy, ExternalLink } from 'lucide-react';
import { invalidateAccountFactors } from './account-factors';
import {
  AUTHENTICATOR_STEP_UP_ROWS as ROWS,
  AUTHENTICATOR_WORDS as WORDS,
  groupedSecretKey,
} from './authenticator-copy';
import { AuthenticatorQr } from './authenticator-qr';
import { keepNumbersWhole } from './keep-numbers-whole';
import { detectPasskeyDevice, type PasskeyDevice } from './passkey-device';
import { RecoveryCodesView } from './recovery-codes-view';
import { signInLockNow } from './sign-in-lock';

/** What a console hands the authenticator surfaces: its LIVE session's enrolment primitives. */
export interface AuthenticatorControls {
  /** The signed-in account's uid (the account reads are keyed by it). */
  uid: string | null;
  /** The live session's own first factor (`firebase.sign_in_provider`); `null` when unreadable. */
  sessionSignInProvider: () => Promise<string | null>;
  /** Start enrolment for the live session (`@givmo/auth`'s `startTotpEnrollment`). */
  start: () => Promise<TotpEnrollment>;
  /** Remove the account's authenticator app (`@givmo/auth`'s `unenrollTotp`). */
  turnOff: () => Promise<void>;
}

/** The live session cannot make an authenticator change: it did not sign in with a provider. */
export class AuthenticatorSessionNotEligible extends Error {
  constructor() {
    super('This session cannot change an authenticator app; confirm with a provider first.');
    this.name = 'AuthenticatorSessionNotEligible';
    Object.setPrototypeOf(this, AuthenticatorSessionNotEligible.prototype);
  }
}

/**
 * Throw unless the live session signed in with Google, Apple or Microsoft — the only sessions the
 * web makes an authenticator change from.
 */
export async function requireProviderSession(controls: AuthenticatorControls): Promise<void> {
  if (!canEnrollTotpFrom(await controls.sessionSignInProvider())) {
    throw new AuthenticatorSessionNotEligible();
  }
}

/** The ways the sheet may confirm an authenticator change with: the account's providers only. */
const PROVIDER_WAYS = ['google', 'apple', 'microsoft'] as const;

/**
 * The gate's client refusal for an authenticator change: a session that is not a provider sign-in,
 * Identity Platform's recent-sign-in rule, or its first-factor refusal. The row names the act.
 */
export function authenticatorClientRefusal(row: string): StepUpClientRefusal {
  return (error) => {
    const needsProvider =
      error instanceof AuthenticatorSessionNotEligible ||
      (error instanceof AuthError &&
        (error.kind === 'requiresRecentLogin' || error.kind === 'unsupportedFirstFactor'));
    return needsProvider ? { row, accepts: PROVIDER_WAYS } : null;
  };
}

/**
 * The sheet's words for the authenticator acts (approved, D-AS-35 fix 9). Setting up and turning off
 * name the only ways that answer them: Identity Platform refuses a passkey, handed-off or
 * re-confirmed session, and the consoles offer no Apple sign-in.
 */
export const AUTHENTICATOR_STEP_UP_COPY: StepUpCopyTable = {
  [ROWS.setUp]: {
    description: () =>
      'Setting up an authenticator app needs a recent sign-in with Google or Microsoft. Confirm it’s you and we’ll set it up.',
  },
  [ROWS.turnOff]: {
    description: () =>
      'Turning off your authenticator app needs a recent sign-in with Google or Microsoft. Confirm it’s you and we’ll turn it off.',
  },
  // The backend's step-up on replacing the codes: iOS's approved title, and fix 9's body in D-AS-29
  // fix 10's pattern (nothing was entered, so "nothing you entered is lost" said nothing).
  [ROWS.replaceCodes]: {
    title: 'Confirm it’s you to replace your recovery codes',
    description: () =>
      'Replacing your recovery codes needs a recent sign-in. Confirm it’s you and we’ll make a new set.',
  },
};

/** The words a failed authenticator call shows. */
export function authenticatorFailureLine(thrown: unknown): string {
  if (thrown instanceof AuthError && thrown.message.length > 0) return thrown.message;
  if (isGivmoError(thrown) && thrown.message.length > 0) return thrown.message;
  return WORDS.failed;
}

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
const LINE = 'font-sans text-s14 font-w400 tracking-givmo text-text-secondary';
const STEP = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';
const CODE_LENGTH = 6;
const PREFIX = 'authenticator-setup';

/**
 * How one setup ended. `locked`: a sign-in lock binds the account (AS-WEB23), so nothing was
 * attempted.
 */
export type AuthenticatorSetupOutcome = 'enrolled' | 'cancelled' | 'locked' | 'failed';

interface FlowProps {
  enrollment: TotpEnrollment;
  device: PasskeyDevice;
  /** The factor now exists: from here only "I've saved these" may close the dialog. */
  onEnrolled: () => void;
  /** Settled once: enrolled after the codes were acknowledged (or could not be shown), else cancelled. */
  onDone: (outcome: Exclude<AuthenticatorSetupOutcome, 'failed'>) => void;
}

/** The two-step flow inside the setup dialog: A2, then A3. */
function AuthenticatorSetupFlow({ enrollment, device, onEnrolled, onDone }: FlowProps) {
  const queryClient = useQueryClient();
  const phone = device === 'iphone' || device === 'android';
  const [showKey, setShowKey] = useState(phone);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [codes, setCodes] = useState<{ list: readonly string[]; notice?: string } | null>(null);

  const turnOn = async () => {
    if (busy || code.length !== CODE_LENGTH) return;
    setBusy(true);
    setRefused(false);
    setError(null);
    try {
      await enrollment.finish(code);
    } catch (thrown) {
      setCode('');
      if (thrown instanceof AuthError && thrown.kind === 'invalidVerificationCode')
        setRefused(true);
      else setError(authenticatorFailureLine(thrown));
      setBusy(false);
      return;
    }
    onEnrolled();
    // The factor exists now. Mirror it before anything renders "on"; a failed sync is caught up by
    // the next security read, so the enrolment still succeeded.
    await sdk.userSecuritySyncMyFactors({ throwOnError: true }).catch(() => undefined);
    void invalidateAccountFactors(queryClient);
    try {
      const { data } = await sdk.userSecurityMintMyRecoveryCodes({ throwOnError: true });
      setCodes({ list: data.recovery_codes });
    } catch (thrown) {
      // Enrolled either way: say why there are no codes to show, and finish.
      const issued = isGivmoError(thrown) && thrown.status === 409;
      setCodes({ list: [], notice: issued ? WORDS.codesAlreadyIssued : WORDS.codesUnavailable });
    } finally {
      setBusy(false);
    }
  };

  if (codes !== null) {
    if (codes.list.length === 0) {
      return (
        <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-no-codes`)}>
          <Dialog.Title className={TITLE}>{WORDS.codesTitle}</Dialog.Title>
          <Dialog.Description className={LINE}>{codes.notice}</Dialog.Description>
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            onClick={() => onDone('enrolled')}
            testId={`${PREFIX}-done`}
          >
            {WORDS.saved}
          </Button>
        </div>
      );
    }
    return <RecoveryCodesView codes={codes.list} onSaved={() => onDone('enrolled')} />;
  }

  const key = groupedSecretKey(enrollment.secretKey);
  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-flow`)}>
      <Dialog.Title className={TITLE}>{WORDS.setUpTitle}</Dialog.Title>
      <Dialog.Description className={LINE}>{keepNumbersWhole(WORDS.setUpLine)}</Dialog.Description>

      <section className="flex flex-col gap-12" aria-label={WORDS.stepAdd}>
        <p className={STEP}>
          <span className="mr-6 text-text-tertiary">1.</span>
          {WORDS.stepAdd}
        </p>
        {phone ? (
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            fullWidth
            leadingIcon={<Icon icon={ExternalLink} size={16} aria-hidden />}
            onClick={() => window.location.assign(enrollment.otpauthUri)}
            testId={`${PREFIX}-open-app`}
          >
            {WORDS.openInApp}
          </Button>
        ) : (
          <div className="flex items-center gap-20 max-sm:flex-col max-sm:items-start">
            <div className="shrink-0 rounded-lg border border-border-subtle bg-white p-12">
              <AuthenticatorQr
                value={enrollment.otpauthUri}
                label={WORDS.qrLabel}
                testId={`${PREFIX}-qr`}
              />
            </div>
            <div className="flex flex-col items-start gap-8">
              <p className={LINE}>{WORDS.scanLine}</p>
              {!showKey && (
                <button
                  type="button"
                  className={LINK}
                  onClick={() => setShowKey(true)}
                  {...testIdProp(`${PREFIX}-cant-scan`)}
                >
                  {WORDS.cantScan}
                </button>
              )}
            </div>
          </div>
        )}
        {showKey && (
          <div className="flex flex-col gap-6">
            {phone && <p className="font-sans text-s13 text-text-secondary">{WORDS.typeKey}</p>}
            <div className="flex items-center justify-between gap-12 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12">
              <span
                className="font-sans text-s16 font-w600 tracking-wider text-text-strong"
                {...testIdProp(`${PREFIX}-key`)}
              >
                {key}
              </span>
              <button
                type="button"
                className={cn(LINK, 'inline-flex shrink-0 items-center gap-4')}
                onClick={() => {
                  setCopyFailed(false);
                  void navigator.clipboard
                    ?.writeText(enrollment.secretKey)
                    .catch(() => setCopyFailed(true));
                }}
                {...testIdProp(`${PREFIX}-copy-key`)}
              >
                <Icon icon={Copy} size={14} aria-hidden />
                {WORDS.copy}
              </button>
            </div>
            {copyFailed && (
              <ErrorMessage className="pt-0" testId={`${PREFIX}-copy-failed`}>
                {WORDS.failed}
              </ErrorMessage>
            )}
          </div>
        )}
      </section>

      <form
        className="flex flex-col gap-12"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void turnOn();
        }}
        {...testIdProp(`${PREFIX}-form`)}
      >
        <p className={STEP}>
          <span className="mr-6 text-text-tertiary">2.</span>
          {keepNumbersWhole(WORDS.stepCode)}
        </p>
        <div className="flex flex-col">
          <OtpField
            length={CODE_LENGTH}
            value={code}
            onValueChange={(next) => {
              setCode(next);
              if (next.length > 0) setRefused(false);
            }}
            error={refused}
            aria-label={WORDS.codeLabel}
            testId={`${PREFIX}-code`}
          />
          {refused && (
            <ErrorMessage testId={`${PREFIX}-code-refused`}>{WORDS.codeRefused}</ErrorMessage>
          )}
          {error !== null && <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>}
        </div>
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {WORDS.warning}
        </p>
        <div>
          <Button
            type="submit"
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth={phone}
            loading={busy}
            disabled={code.length !== CODE_LENGTH}
            testId={`${PREFIX}-turn-on`}
          >
            {WORDS.turnOn}
          </Button>
        </div>
      </form>
      <div className="flex items-center">
        <button
          type="button"
          className={LINK}
          disabled={busy}
          onClick={() => onDone('cancelled')}
          {...testIdProp(`${PREFIX}-back`)}
        >
          {WORDS.back}
        </button>
      </div>
    </div>
  );
}

export interface AuthenticatorSetup {
  /** Run one setup: the sheet if the session needs it, then A2 and A3. Never rejects. */
  start: () => Promise<AuthenticatorSetupOutcome>;
  pending: boolean;
  /** The failure line of the last attempt, if it did not get as far as the secret. */
  failure: string | null;
  /** The gate's sheet and the setup dialog: render beside the control. */
  dialog: ReactNode;
}

/** Setting up an authenticator app from a control (the card's "Set up", A1's alternative). */
export function useAuthenticatorSetup(
  controls: AuthenticatorControls | undefined,
  {
    testIdPrefix = 'authenticator-step-up',
    device,
  }: { testIdPrefix?: string; device?: PasskeyDevice } = {},
): AuthenticatorSetup {
  const queryClient = useQueryClient();
  const clientRefusal = useMemo(() => authenticatorClientRefusal(ROWS.setUp), []);
  const gate = useStepUpGate({ copy: AUTHENTICATOR_STEP_UP_COPY, testIdPrefix, clientRefusal });
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  /** The factor exists: the codes are on screen, and only "I've saved these" leaves them. */
  const [locked, setLocked] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const settleRef = useRef<((outcome: AuthenticatorSetupOutcome) => void) | null>(null);
  const resolvedDevice = useMemo(() => device ?? detectPasskeyDevice(), [device]);
  const { run } = gate;

  const start = useCallback(async (): Promise<AuthenticatorSetupOutcome> => {
    if (controls === undefined) return 'failed';
    setPending(true);
    setFailure(null);
    let started: TotpEnrollment;
    try {
      const lock = await signInLockNow(queryClient, controls.uid);
      if (lock === 'locked') return 'locked';
      if (lock === 'no_answer') {
        setFailure(WORDS.failed);
        return 'failed';
      }
      started = await run(async () => {
        await requireProviderSession(controls);
        return controls.start();
      });
    } catch (thrown) {
      if (isStepUpCancelled(thrown)) return 'cancelled';
      setFailure(authenticatorFailureLine(thrown));
      return 'failed';
    } finally {
      setPending(false);
    }
    return new Promise<AuthenticatorSetupOutcome>((resolve) => {
      settleRef.current = resolve;
      setEnrollment(started);
    });
  }, [controls, run, queryClient]);

  const finish = useCallback((outcome: Exclude<AuthenticatorSetupOutcome, 'failed'>) => {
    setEnrollment(null);
    setLocked(false);
    settleRef.current?.(outcome);
    settleRef.current = null;
  }, []);

  return {
    start,
    pending,
    failure,
    dialog: (
      <>
        {gate.dialog}
        {enrollment !== null && (
          <Dialog.Root
            open
            onOpenChange={(next) => {
              // The codes are shown once: only "I've saved these" leaves them. Before the factor
              // exists, closing abandons the setup.
              if (!next && !locked) finish('cancelled');
            }}
          >
            <Dialog.Content
              width={560}
              className="flex flex-col gap-16"
              testId={`${PREFIX}-dialog`}
            >
              <AuthenticatorSetupFlow
                enrollment={enrollment}
                device={resolvedDevice}
                onEnrolled={() => setLocked(true)}
                onDone={finish}
              />
            </Dialog.Content>
          </Dialog.Root>
        )}
      </>
    ),
  };
}
